import { DropdownItemProps } from '@ui/elements/Dropdown/types'
import { useTranslation } from 'react-i18next'
import { getLanguageName, LanguageCode } from '@ca/common'
import LanguageAction from './LanguageAction'
import { useAppRegion } from 'storage/useRemoteConfigParams'
import { useStagesStore } from 'hooks/useStagesStore'
import { usePastDeals } from 'hooks/usePastDeals'
import { useStaffNavigate } from 'hooks/useIntroStore'
import { router, usePathname } from 'expo-router'
import { useLogout } from 'hooks/useLogout'
import useRestartOrder from '@order/hooks/useRestartOrder'
import { useCustomer, useStaff } from 'hooks/useSession'
import { useModalState } from '@order/hooks/useModalDataState'

export type ConfirmModalState = {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  desc: string
}

export type DropdownEntry = DropdownItemProps & { modal?: ConfirmModalState }

const useDropdownStaffPanel = () => {
  const { t } = useTranslation(['ui'])
  const navigate = useStaffNavigate()
  return {
    label: t('ui:staffPanel'),
    icon: 'shield-check-filled',
    onPress: () => navigate('/staff/panel'),
  } satisfies DropdownItemProps
}

const useDropdownCustomerAccount = () => {
  const { t } = useTranslation(['ui'])
  return {
    label: t('ui:customerAccount'),
    icon: 'user-circle',
    onPress: () => {
      router.navigate('/customer/account')
    },
  } satisfies DropdownItemProps
}

export const useDropdownPastDeals = (redirect?: string | null) => {
  const { t } = useTranslation(['ui'])
  const pastDeals = usePastDeals()
  return {
    // disabled: showPastDeals,
    label: pastDeals.value ? t('ui:dealsPastHide') : t('ui:dealsPastShow'),
    icon: pastDeals.value ? 'stack-2' : 'stack-2-filled',
    onPress() {
      pastDeals.toggle({
        redirect: redirect === null ? undefined : redirect ?? '/deals',
      })
    },
  } satisfies DropdownItemProps
}

const useDropdownBackToDeals = () => {
  const { t } = useTranslation(['ui'])
  return {
    label: t('ui:backToDeals'),
    icon: 'arrow-back',
    onPress: () => {
      router.navigate('/deals')
    },
  } satisfies DropdownItemProps
}

const useDropdownQuickLogin = () => {
  const { t } = useTranslation(['ui'])
  const navigate = useStaffNavigate()
  return {
    label: t('ui:quickLogin'),
    icon: 'login',
    onPress: () => navigate('/staff/quick-login'),
  } satisfies DropdownItemProps
}

const useDropdownBackToCheckout = () => {
  const { t } = useTranslation(['ui', 'modals'])
  const pathname = usePathname()
  const isCheckout = pathname === '/checkout'
  // const { list: orders } = useCartItems()
  // const isOrderSingle = orders.length === 1
  const confirm = useModalState()

  return {
    label: t('ui:backToCheckout'),
    icon: 'arrow-back',
    onPress: confirm.open,
    destructive: true,
    disabled: isCheckout,
    modal: {
      isOpen: confirm.isOpen,
      onClose: confirm.close,
      onConfirm: () => {
        router.navigate('/checkout')
        confirm.close()
      },
      title: t('modals:backToCheckout.title'),
      desc: t('modals:backToCheckout.desc'),
    } satisfies ConfirmModalState,
  } satisfies DropdownEntry
}

const useDropdownRestartConfiguration = () => {
  const { t } = useTranslation(['ui', 'modals'])
  const { restart } = useRestartOrder()
  const pathname = usePathname()
  const isCheckout = pathname === '/checkout'
  const confirm = useModalState()

  return {
    label: t('ui:restartConfiguration'),
    icon: 'repeat',
    onPress: confirm.open,
    destructive: true,
    disabled: isCheckout,

    modal: {
      isOpen: confirm.isOpen,
      onClose: confirm.close,
      onConfirm: () => {
        restart()
        confirm.close()
      },
      title: t('modals:restartConfiguration.title'),
      desc: t('modals:restartConfiguration.desc'),
    } satisfies ConfirmModalState,
  } satisfies DropdownEntry
}

const useDropdownLogout = () => {
  const { t } = useTranslation(['ui', 'modals'])
  const logout = useLogout()
  const staff = useStaff()
  const customer = useCustomer()
  const confirm = useModalState()

  return {
    label: t('ui:logout'),
    icon: 'logout',
    onPress: confirm.open,
    destructive: true,
    disabled: !staff && !customer,
    modal: {
      isOpen: confirm.isOpen,
      onClose: confirm.close,
      onConfirm: () => {
        logout()
        confirm.close()
      },
      title: t(
        customer ? 'modals:logout.customer.title' : 'modals:logout.staff.title'
      ),
      desc: t(
        customer ? 'modals:logout.customer.desc' : 'modals:logout.staff.desc'
      ),
    } satisfies ConfirmModalState,
  } satisfies DropdownEntry
}

const useDropdownLanguage = () => {
  const { i18n } = useTranslation()
  const locale = i18n.language as LanguageCode
  const region = useAppRegion()
  return {
    label: getLanguageName(locale),
    iconComponent: <LanguageAction language={locale} />,
    items: region.languages.interface.map((lang) => {
      return {
        label: getLanguageName(lang),
        iconComponent: <LanguageAction language={lang} />,
        onPress() {
          i18n.changeLanguage(lang)
        },
        hidden: lang === locale,
        key: `lang-${lang}`,
      }
    }),
    key: `lang-${locale}`,
  } satisfies DropdownEntry
}

const useDropdownDevUI = () => {
  return {
    label: 'UI Elements',
    icon: 'components',
    onPress: () => router.navigate('/dev/ui'),
  } satisfies DropdownItemProps
}

export default function useDropdownStage() {
  const dropdown = useStagesStore((state) => state.dropdown)

  const staffPanel = useDropdownStaffPanel()
  const quickLogin = useDropdownQuickLogin()
  const customerAccount = useDropdownCustomerAccount()
  const pastDeals = useDropdownPastDeals()
  const pastDealsConfig = useDropdownPastDeals('/configuration/deal/select')
  const backToDeals = useDropdownBackToDeals()
  const restartConfiguration = useDropdownRestartConfiguration()
  const backToCheckout = useDropdownBackToCheckout()
  const logout = useDropdownLogout()
  const language = useDropdownLanguage()
  const devUI = useDropdownDevUI()

  const items: Record<string, DropdownEntry[]> = {
    empty: [],
    intro: [staffPanel, quickLogin, pastDeals, language, logout],
    deals: [staffPanel, quickLogin, pastDeals, language, logout],
    staff: [backToDeals, /* staffPanel, */ language, logout],
    customer: [customerAccount, staffPanel, language, logout],
    main: [backToCheckout, customerAccount, staffPanel, language, logout],
    avatar: [
      restartConfiguration,
      customerAccount,
      staffPanel,
      language,
      logout,
    ],
    configDeals: [
      backToCheckout,
      customerAccount,
      staffPanel,
      pastDealsConfig,
      language,
      logout,
    ],
    dev: [devUI, staffPanel, language, logout],
  }

  return items[dropdown] ?? []
}
